import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, PlugZap, ListTree, ShieldCheck, Settings, LogOut, TrendingUp, ShieldAlert, Radar, Users, DollarSign, Mailbox, AlertCircle, Clock, MailCheck } from "lucide-react";
import { RecrystaMark } from "./RecrystaLogo";

const NAV = [
  { to: "/dashboard/forecast", label: "Forecast", icon: TrendingUp, tid: "nav-forecast" },
  { to: "/dashboard/risk", label: "Risk Radar", icon: ShieldAlert, tid: "nav-risk" },
  { to: "/dashboard/funnel", label: "Funnel X-Ray", icon: Radar, tid: "nav-funnel" },
  { to: "/dashboard/team", label: "Team", icon: Users, tid: "nav-team" },
  { to: "/dashboard/bookings", label: "Bookings", icon: DollarSign, tid: "nav-bookings" },
  { to: "/dashboard/briefings", label: "Briefings", icon: Mailbox, tid: "nav-briefings" },
  { to: "/dashboard/health", label: "Data Health", icon: ShieldCheck, tid: "nav-health" },
  { to: "/dashboard/mapping", label: "Stage Mapping", icon: ListTree, tid: "nav-mapping" },
  { to: "/dashboard/connect", label: "Connect", icon: PlugZap, tid: "nav-connect" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

export default function DashboardShell({ children }) {
  const { account, user, billing, logout } = useAuth();
  const nav = useNavigate();
  const [resendBusy, setResendBusy] = useState(false);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  const resendVerification = async () => {
    if (resendBusy) return;
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch (e) {
      toast.error("Couldn't send verification email — try again shortly.");
    } finally {
      setResendBusy(false);
    }
  };

  const showPaymentBanner = billing?.payment_failed || billing?.state === "past_due";
  const showTrialBanner =
    billing?.state === "trialing" && (billing?.trial_days_left ?? 99) <= 3;
  const showVerifyBanner = user && user.email_verified === false;

  return (
    <div className="flex h-screen w-full bg-slate-50 overflow-hidden" data-testid="dashboard-shell">
      <aside className="w-64 border-r border-slate-200 bg-white flex flex-col">
        <div className="px-6 py-6 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <RecrystaMark size={32} />
            <div>
              <div className="text-sm font-bold tracking-tight text-slate-900">
                Recrysta
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">
                {account?.name || "Workspace"}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-3">
          <div className="px-3 py-2 mb-1">
            <div className="text-xs font-medium text-slate-900 truncate">{user?.name || user?.email}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden">
        {showVerifyBanner && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="verify-email-banner"
          >
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <MailCheck className="h-4 w-4 flex-shrink-0" />
              <span>
                Please verify your email address ({user.email}) — briefings won&apos;t send until you do.
              </span>
            </div>
            <button
              onClick={resendVerification}
              disabled={resendBusy}
              data-testid="verify-banner-resend"
              className="text-xs font-semibold text-amber-900 underline hover:no-underline whitespace-nowrap disabled:opacity-60"
            >
              {resendBusy ? "Sending…" : "Resend link →"}
            </button>
          </div>
        )}
        {showPaymentBanner && (
          <div
            className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="payment-failed-banner"
          >
            <div className="flex items-center gap-2 text-sm text-red-900">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                {billing.message ||
                  "Your last payment failed — update your card to keep access to Recrysta."}
              </span>
            </div>
            <NavLink
              to="/dashboard/settings"
              className="text-xs font-semibold text-red-900 underline hover:no-underline whitespace-nowrap"
              data-testid="payment-banner-cta"
            >
              Update billing →
            </NavLink>
          </div>
        )}
        {showTrialBanner && !showPaymentBanner && (
          <div
            className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-3"
            data-testid="trial-banner"
          >
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>
                Your free trial ends in{" "}
                <span className="font-semibold">
                  {billing.trial_days_left} day{billing.trial_days_left === 1 ? "" : "s"}
                </span>{" "}
                — pick a plan to keep uninterrupted access.
              </span>
            </div>
            <NavLink
              to="/dashboard/settings"
              className="text-xs font-semibold text-amber-900 underline hover:no-underline whitespace-nowrap"
              data-testid="trial-banner-cta"
            >
              Choose a plan →
            </NavLink>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
